/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Typography, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { InputLabel, Select, Textarea, AvailabilitySelector } from 'components/elements';
import { useFetchTimeZones, useFetchLocations, useProfile, useFetchAppointments } from 'hooks';
import { validators } from 'utils';
import { periods } from 'const';
import ProgressSubtitle from './ProgressSubtitle';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function RequestAppointmentForm({ onSubmit, providerAccountId, providerName, healthcareServiceId, ...restProps }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { data: locations } = useFetchLocations(providerAccountId, healthcareServiceId);
  const { data: profile } = useProfile();
  const { data: timezones } = useFetchTimeZones();
  const { data: appointments } = useFetchAppointments({ period: periods.history, healthcareServiceId });

  const { handleSubmit, control, setValue, watch } = useForm({
    mode: 'onSubmit',
    defaultValues: { timezone: profile?.timezone, locationId: '', ...restProps },
  });
  const locationId = watch('locationId');
  useEffect(() => {
    if (!locationId) {
      if (locations.length > 0 && appointments.length > 0) {
        const prevAppointment = appointments.find(({ attributes }) => attributes.providerAccountId === providerAccountId && attributes.healthcareServiceId === healthcareServiceId);
        const previousAppointmentLocationId = locations.find(({ value }) => prevAppointment?.attributes.locationId === value)?.value;
        if (previousAppointmentLocationId) {
          setValue('locationId', previousAppointmentLocationId);
        }
      }
    }
  }, [locations, appointments]);

  const formStyles = cn(classes.bookAppointmentForm, classes.bookAppointmentFormWithAvailability);

  return (
    <form
      className={formStyles}
      onSubmit={handleSubmit((data, ...rest) => {
        const location = locations.find((x) => x.value === data.locationId);
        onSubmit({ ...data, location }, ...rest);
      })}
    >
      <ProgressSubtitle>
        <>
          <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
            {t('follow_up_appointment')}
          </Typography>{' '}
          <Typography variant="h6" component="span">
            {t('with')}
          </Typography>{' '}
          <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
            {providerName}
          </Typography>
        </>
      </ProgressSubtitle>

      <div className={classes.selectorsWrapper}>
        <div className={classes.selector}>
          <Controller
            name="locationId"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('please_fill_in_this_field'),
              },
            }}
            render={({ field: { ref, ...restField }, fieldState: { error } }) => (
              <Select options={locations} label={<InputLabel text={t('location')} required />} helperText={error ? error.message : null} error={!!error} {...restField} />
            )}
          />
        </div>
        <div className={classes.selector}>
          <Controller
            name="timezone"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('please_fill_in_this_field'),
              },
            }}
            render={({ field: { ref, ...restField }, fieldState: { error } }) => (
              <Select options={timezones} label={<InputLabel text={t('timezone')} required />} helperText={error ? error.message : null} error={!!error} {...restField} />
            )}
          />
        </div>
      </div>

      <div className={classes.selectorsWrapper}>
        <div className={classes.availabilitySelector}>
          <Controller
            name="availabilityPreferences"
            control={control}
            rules={{
              validate: (value) => {
                if (!validators.availabilityValidator(value, true)) return t('please_fill_in_this_field');
                return null;
              },
            }}
            render={({ field: { ref, ...restField }, fieldState: { error } }) => (
              <AvailabilitySelector
                isMorningEnabled
                isAfternoonEnabled
                isEveningEnabled
                label={
                  <>
                    <InputLabel text={t('please_select_your_availability')} required />
                    <Typography className={classes.availabilityLabel} variant="body2" component="p">
                      {t('please_indicate_all_times_during_which_you_are_available_for_scheduling')}
                    </Typography>
                  </>
                }
                helperText={error ? error.message : null}
                error={!!error}
                {...restField}
              />
            )}
          />
        </div>
      </div>

      <Controller
        name="availabilityPreferencesDetail"
        control={control}
        render={({ field: { ref, ...restField } }) => <Textarea label={<InputLabel text={t('additional_information')} />} hasLimitCounter={false} {...restField} />}
      />
      <Button type="submit" variant="contained" color="primary" size="large" className={classes.submitButton}>
        {t('next')}
      </Button>
    </form>
  );
}

RequestAppointmentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  providerAccountId: PropTypes.string,
  providerName: PropTypes.string,
  healthcareServiceId: PropTypes.string,
};

RequestAppointmentForm.defaultProps = {
  providerAccountId: '',
  providerName: '',
  healthcareServiceId: '',
};

export default RequestAppointmentForm;
